<template>
  <PopinBase size="3xs" :is-active="isActive" :z="300" @close="emit('close')">
    <template #body>
      <div class="p-3 space-y-2 pb-6">
        <div class="p-3 bg-charcoal-100 dark:bg-dark-800 rounded-xl" v-for="[ reaction, users ] in reactionList">
          <div class="font-bold flex items-center space-x-1">
            <ImageEmoji :name="REACTIONS[reaction]?.name" class="size-5 shrink-0" size="sm" />
            <p class="grow text-sm">{{ $t('components.Reactions.reactionCount', users.length) }}</p>
          </div>

          <div class="flex flex-wrap gap-1 mt-3">
            <UserPicture
              v-for="user in users.filter(u => u)"
              size="md"
              is-link
              :user="user"
            />
          </div>
        </div>
      </div>
    </template>
  </PopinBase>
</template>

<script lang="ts" setup>
import { REACTIONS } from '@/static/reactions'
import { IUser, Reaction } from '@/classes'

const props = withDefaults(defineProps<{
  isActive: boolean
  reactions?: Reaction[]
}>(), {
  reactions: () => []
})

const { reactions, isActive } = toRefs(props)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const { fetchUsersByIds } = useDataFetch()
const { result: users, refresh } = await fetchUsersByIds(
  'reaction-users',
  computed(() => reactions.value.map(reaction => reaction.owner)), undefined,
  {
    lazy: true,
    retry: false,
    immediate: false
  }
)

watch([ isActive, reactions ], () => {
  if (isActive.value && reactions.value.length > 0) refresh()
})

const reactionList = computed(() => {
  const reactionTypes: Map<keyof typeof REACTIONS, (IUser | null)[]> = new Map()

  reactions.value.forEach(reaction => {
    if (!reactionTypes.has(reaction.value)) {
      reactionTypes.set(reaction.value, [])
    }

    reactionTypes.get(reaction.value)?.push(users.value.find(user => user._id === reaction.owner) ?? null)
  })

  return [ ...reactionTypes.entries() ].sort((a, b) => b[1].length - a[1].length)
})
</script>